import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Reveal from "./Reveal";

const BLOCKS = [
  { t: "p", x: "Once upon a time, there were two girls from Haugesund who discovered, quite early on, that the world could become bigger if they simply invented it themselves.\nThey had their own names, of course.\nBut sometimes, they were Caroline and Line." },
  { t: "p", x: "Many years later, traces of those two girls are still there.\nIn a four-leaf clover found in the forest, as though the world still hides small secrets for those who know to look.\nIn the beech seeds and dried flowers — things that once lived, fell to the ground and changed their form, but never truly disappeared." },
  { t: "img", src: "/media/cocreation-1.jpg", alt: "The co-creation mobile of driftwood, brass hoops, dried flowers, clover and stones hanging in warm window light" },
  { t: "p", x: "And somewhere among all of this hangs a small figure made of twigs.\nAlmost like a person.\nOr perhaps it is simply the part of us that never quite grows up.\nThe part that still remembers how to make a whole world out of almost nothing." },
  { t: "img", src: "/media/cocreation-2.jpg", alt: "Close-up of driftwood bark, dried beech flowers, a four-leaf clover in a brass ring and rose quartz" },
  { t: "p", x: "The mobile moves when the air around it moves. Its pieces drift closer to one another, then apart again.\nA little like two lives do." },
  { t: "pull", x: "But some people remain somewhere inside our story.\nBecause they were there while we were becoming who we are." },
  { t: "p", x: "And somewhere, when the mobile begins to move and all the little things start dancing towards one another again, you can almost hear two girls from Haugesund." },
  { t: "pull", x: "But they are still laughing ❤️" },
  { t: "note", x: "The full tale lives with the piece — and will be shared, in time, in a letter from the atelier." },
];

function Lines({ text }) {
  return text.split("\n").map((line, j) => (
    <span key={j}>
      {j > 0 && <br />}
      {line}
    </span>
  ));
}

export default function CoCreationPage() {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Co-Creation No. 001 — The Tale of Caroline and Line";
    return () => {
      document.title = "The Things We Keep — Objects with a story";
    };
  }, []);

  return (
    <div className="bg-paper pb-24 pt-32 sm:pb-32 sm:pt-40" data-testid="cocreation-page">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <p className="ttwk-label" data-testid="cocreation-eyebrow">
            Co-Creation No. 001
          </p>
          <h1 className="mt-6 max-w-2xl font-serif text-5xl font-light leading-[1.05] tracking-tight sm:text-7xl" data-testid="cocreation-title">
            The Tale of Caroline and Line
          </h1>
        </Reveal>

        <div className="mt-16 max-w-2xl">
          {BLOCKS.map((b, i) => {
            if (b.t === "img") {
              return (
                <Reveal key={i}>
                  <div className="img-frame my-16 overflow-hidden rounded-sm border border-clay/60">
                    <img src={b.src} alt={b.alt} className="w-full object-cover" data-testid={`cocreation-image-${i}`} />
                  </div>
                </Reveal>
              );
            }
            if (b.t === "note") {
              return (
                <Reveal key={i}>
                  <p className="mt-10 text-sm italic leading-relaxed text-umber/60" data-testid="cocreation-note">
                    {b.x}
                  </p>
                </Reveal>
              );
            }
            if (b.t === "pull") {
              return (
                <Reveal key={i}>
                  <p className="my-14 text-center font-serif text-3xl font-light italic leading-snug text-terracotta sm:text-4xl">
                    <Lines text={b.x} />
                  </p>
                </Reveal>
              );
            }
            return (
              <Reveal key={i} delay={Math.min(i * 0.03, 0.15)}>
                <p className="mt-8 font-serif text-xl font-light leading-relaxed text-umber first:mt-0 sm:text-2xl">
                  <Lines text={b.x} />
                </p>
              </Reveal>
            );
          })}
        </div>

        <Reveal>
          <button
            onClick={() => navigate("/", { state: { scrollTo: "commission" } })}
            className="mt-16 rounded-full bg-terracotta px-10 py-4 text-[11px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-espresso"
            data-testid="cocreation-cta"
          >
            Create together
          </button>
        </Reveal>
      </div>
    </div>
  );
}
